/**
 * One recommendation's correspondence: every letter between the issuer and its addressees, oldest
 * first so it reads as the exchange happened, with the issuer's assessment (the letter that graded
 * the response) marked. Shown in the register's dialog and on a recommendation's own tab.
 */

import React from 'react';
import {Alert, Box, Chip, CircularProgress, Paper, Stack, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {SafetyRecommendationLetterDTO, SafetyRecommendationStatusGroup} from '../../@types/safety-recommendation';
import {STATUS_STYLE} from './RecommendationOverview';

export interface CorrespondenceListProps {
    /** The letters, oldest first as the server sends them; null while loading. */
    letters: SafetyRecommendationLetterDTO[] | null;
    error: string | null;
    /** Colours the assessment letter with the recommendation's status. */
    group: SafetyRecommendationStatusGroup;
}

const direction = (letter: SafetyRecommendationLetterDTO): string =>
    letter.fromIssuer ? `NTSB to ${letter.addressee || 'addressee'}` : `${letter.addressee || 'Addressee'} to NTSB`;

const CorrespondenceList: React.FC<CorrespondenceListProps> = ({letters, error, group}) => {
    const color = STATUS_STYLE[group].color;
    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (letters === null) {
        return <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}><CircularProgress size={28} /></Box>;
    }
    if (letters.length === 0) {
        return <Typography variant="body2" color="text.secondary">No letters on record.</Typography>;
    }
    return (
        <Stack spacing={1.5}>
            <Typography variant="overline" color="text.secondary" sx={{lineHeight: 1.5}}>
                {`${letters.length} letter${letters.length === 1 ? '' : 's'}, oldest first`}
            </Typography>
            {letters.map((letter, index) => {
                const {assessment} = letter;
                return (
                    <Paper
                        key={`${letter.date}-${index}`}
                        variant="outlined"
                        data-testid={assessment ? 'assessment-letter' : undefined}
                        sx={{
                            p: 1.5,
                            borderLeft: assessment ? `4px solid ${color}` : undefined,
                            bgcolor: assessment ? alpha(color, 0.05) : undefined,
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: 0.75}}>
                            <Typography variant="subtitle2">{letter.date}</Typography>
                            <Typography variant="body2" color="text.secondary">{direction(letter)}</Typography>
                            {letter.type && <Chip size="small" variant="outlined" label={letter.type} sx={{height: 20}} />}
                            {assessment && <Chip size="small" label="The NTSB's assessment" sx={{height: 20}} />}
                        </Stack>
                        <Typography variant="body2" sx={{whiteSpace: 'pre-line'}}>
                            {letter.contents || 'No text on record.'}
                        </Typography>
                    </Paper>
                );
            })}
        </Stack>
    );
};

export default CorrespondenceList;
